import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axiosInstance";
import { toastApiError } from "../../helper/api/toastApiError";

const unwrapEmails = (value) => {
  const emails = value?.$values ?? value;
  return Array.isArray(emails) ? emails : [];
};

const normalizeSettings = (settings) => ({
  isEnabled: settings?.isEnabled === true,
  recipientEmails: unwrapEmails(settings?.recipientEmails)
    .filter((email) => typeof email === "string" && email.trim())
    .map((email) => email.trim()),
  lastSentReportDate: settings?.lastSentReportDate ?? null,
});

const rejectResponse = (data, thunkApi, fallback) => {
  const message = toastApiError(data, fallback);
  return thunkApi.rejectWithValue({ message });
};

const rejectError = (error, thunkApi, fallback) => {
  const message = toastApiError(error, fallback);
  return thunkApi.rejectWithValue({
    message,
    status: error?.response?.status ?? null,
  });
};

export const getDailyShipmentReportSettings = createAsyncThunk(
  "dailyShipmentReport/getSettings",
  async (_, thunkApi) => {
    try {
      const { data } = await axiosInstance.get(
        "/DailyShipmentReport/GetSettings"
      );

      if (data?.statusCode !== 200) {
        return rejectResponse(
          data,
          thunkApi,
          "Günlük sevkiyat mail ayarları alınamadı."
        );
      }

      return normalizeSettings(data?.data);
    } catch (error) {
      return rejectError(
        error,
        thunkApi,
        "Günlük sevkiyat mail ayarları alınamadı."
      );
    }
  }
);

export const updateDailyShipmentReportSettings = createAsyncThunk(
  "dailyShipmentReport/updateSettings",
  async (settings, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        "/DailyShipmentReport/UpdateSettings",
        settings
      );

      if (data?.statusCode !== 200) {
        return rejectResponse(
          data,
          thunkApi,
          "Günlük sevkiyat mail ayarları kaydedilemedi."
        );
      }

      toast.success("Günlük sevkiyat mail ayarları kaydedildi.");
      return normalizeSettings(data?.data);
    } catch (error) {
      return rejectError(
        error,
        thunkApi,
        "Günlük sevkiyat mail ayarları kaydedilemedi."
      );
    }
  }
);

const initialState = {
  isEnabled: false,
  recipientEmails: [],
  lastSentReportDate: null,
  isLoading: false,
  isSaving: false,
  isLoaded: false,
  error: null,
};

const applySettings = (state, action) => {
  state.isEnabled = action.payload.isEnabled;
  state.recipientEmails = action.payload.recipientEmails;
  state.lastSentReportDate = action.payload.lastSentReportDate;
  state.error = null;
};

const dailyShipmentReportSlice = createSlice({
  name: "dailyShipmentReport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDailyShipmentReportSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDailyShipmentReportSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        applySettings(state, action);
      })
      .addCase(getDailyShipmentReportSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ??
          "Günlük sevkiyat mail ayarları alınamadı.";
      })
      .addCase(updateDailyShipmentReportSettings.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateDailyShipmentReportSettings.fulfilled, (state, action) => {
        state.isSaving = false;
        applySettings(state, action);
      })
      .addCase(updateDailyShipmentReportSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error =
          action.payload?.message ??
          "Günlük sevkiyat mail ayarları kaydedilemedi.";
      });
  },
});

export default dailyShipmentReportSlice.reducer;
